import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TRequestStatus } from "@models/msc.model";
import {
  connectMeta,
  connectTelegram,
  connectWhatsApp,
  connectWhatsAppTestNumber,
  disconnectChannel,
  disconnectMetaPage,
  disconnectWhatsAppNumber,
  renameWhatsAppNumber,
  getChannelConnections,
  setMetaChannelEnabled,
  type TMetaPageChannel,
} from "@/services/channels.service";
import type {
  TChannelConnection,
  TConnectableProvider,
  TConnectTelegramPayload,
  TConnectWhatsAppPayload,
} from "@/models/channels.model";

type ChannelsState = {
  initialized: boolean;
  status: TRequestStatus;
  connections: TChannelConnection[];
};

type ChannelsActions = {
  fetchConnections: (force?: boolean) => Promise<TChannelConnection[]>;
  /** Finishes Connect with Meta with the one-time code from Facebook Login for Business and its dialog redirect. */
  connectMeta: (code: string, redirectUri?: string) => Promise<TChannelConnection>;
  setMetaChannelEnabled: (id: number, channel: TMetaPageChannel, enabled: boolean) => Promise<TChannelConnection>;
  disconnectMetaPage: (id: number) => Promise<TChannelConnection>;
  connectWhatsApp: (payload: TConnectWhatsAppPayload) => Promise<TChannelConnection>;
  /** Development only: Meta's test number, no Embedded Signup. */
  connectWhatsAppTestNumber: () => Promise<TChannelConnection>;
  renameWhatsAppNumber: (id: number, label: string) => Promise<TChannelConnection>;
  disconnectWhatsAppNumber: (id: number) => Promise<TChannelConnection>;
  connectTelegram: (payload: TConnectTelegramPayload) => Promise<TChannelConnection>;
  disconnect: (provider: TConnectableProvider) => Promise<TChannelConnection>;
};

/** Swap the updated connection into the list (matched by provider). */
const upsert = (list: TChannelConnection[], next: TChannelConnection) =>
  list.map((c) => (c.provider === next.provider ? next : c));

export const useChannelsStore = create<ChannelsState & ChannelsActions>()(
  devtools(
    (set, get) => {
      /** Run a mutation and merge its result back into the store. */
      const apply = async (action: string, run: () => Promise<TChannelConnection>) => {
        const updated = await run();
        set({ connections: upsert(get().connections, updated) }, false, action);
        return updated;
      };

      return {
        initialized: false,
        status: "IDLE",
        connections: [],

        fetchConnections: async (force = false) => {
          const { connections, initialized } = get();
          if (initialized && !force) return connections;

          set({ status: "PENDING" }, false, "fetchConnections/pending");
          try {
            const data = await getChannelConnections();
            set({ connections: data, status: "IDLE", initialized: true }, false, "fetchConnections/fulfilled");
            return data;
          } catch (e) {
            set({ status: "REJECTED", initialized: true }, false, "fetchConnections/rejected");
            throw e;
          }
        },

        connectMeta: (code, redirectUri) => apply("connectMeta", () => connectMeta(code, redirectUri)),
        setMetaChannelEnabled: (id, channel, enabled) =>
          apply("setMetaChannelEnabled", () => setMetaChannelEnabled(id, channel, enabled)),
        disconnectMetaPage: (id) => apply("disconnectMetaPage", () => disconnectMetaPage(id)),
        connectWhatsApp: (payload) => apply("connectWhatsApp", () => connectWhatsApp(payload)),
        connectWhatsAppTestNumber: () => apply("connectWhatsAppTestNumber", connectWhatsAppTestNumber),
        renameWhatsAppNumber: (id, label) => apply("renameWhatsAppNumber", () => renameWhatsAppNumber(id, label)),
        disconnectWhatsAppNumber: (id) => apply("disconnectWhatsAppNumber", () => disconnectWhatsAppNumber(id)),
        connectTelegram: (payload) => apply("connectTelegram", () => connectTelegram(payload)),
        disconnect: (provider) => apply("disconnect", () => disconnectChannel(provider)),
      };
    },
    { name: "channels-store" }
  )
);

/** Providers still backed by a local mock; their state isn't real, so they never raise the alert. */
const MOCK_PROVIDERS = new Set(["telegram"]);

/**
 * Connections that broke (token expired / access revoked) and need a reconnect.
 * A card that merely failed to load isn't one: a network blip must not tell the
 * admin their channels are down.
 */
export const selectBrokenConnections = (s: ChannelsState) =>
  s.connections.filter((c) => c.status === "error" && !c.loadFailed && !MOCK_PROVIDERS.has(c.provider));
